import type { PrescriptionCardPayload } from '@chewy/chat-library-common';
import { getFilteredItemTags } from '@chewy/chat-library-common';
import { getMonthDayYear } from '@chewy/chat-library-common/tools/dates';
import { WidgetContainer, ActionButton, AdditionalInfo, OrderStatus, ProductImage, Tags } from '@chewy/chat-library-components';
import first from 'lodash/first';
import styles from './PrescriptionCard.module.scss';

interface PrescriptionCardProps {
    data?: PrescriptionCardPayload | PrescriptionCardPayload[];
    isSelectable?: boolean;
}

const formatPrescriptionDetailValue = (value?: string | null): string => {
    return value || '–';
};

export default function PrescriptionCard({ data, isSelectable = false }: PrescriptionCardProps) {
    if (!data) {
        return null;
    }

    const cardData = Array.isArray(data) ? first(data) : data;
    if (!cardData) {
        return null;
    }

    const {
        prescriptionId = '',
        items = [],
        actions = [],
        additionalMetaData = [],
        prescriptionState = '',
    } = cardData;

    const hasActions = actions.length > 0;
    const hasAdditionalMetaData = additionalMetaData.length > 0;

    // Get the prescription item
    const prescriptionItem = first(items);
    if (!prescriptionItem) {
        return null;
    }

    const { name = '', itemTags, prescriptionDetails = [], imageUrl = '' } = prescriptionItem;
    const prescriptionDetail = prescriptionDetails[0] || {};
    const {
        quantityRemaining = null,
        expirationTime = null,
        rxContextualMessage = '',
    } = prescriptionDetail;

    const status = prescriptionState || '';
    const safeItemTags = Array.isArray(itemTags) ? itemTags.filter((tag): tag is string => typeof tag === 'string') : [];
    const mappedTags = getFilteredItemTags(safeItemTags);

    const formatExpirationDate = (dateStr?: string | null): string => {
        const trimmedDate = dateStr?.trim();

        if (!trimmedDate) return '–';

        return getMonthDayYear(trimmedDate) || trimmedDate;
    };

    const hasPrescriptionDetails = Boolean(prescriptionDetails.length);
    const expirationLabel = status.toLowerCase() === 'expired' ? 'Expired' : 'Expiration';


    return (
        <WidgetContainer
            isSelectable={isSelectable}
            hasActions={hasActions}
            data={cardData}
        >
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.imageContainer}>
                        {imageUrl && <ProductImage imageUrl={imageUrl} name={prescriptionId || name} />}
                    </div>
                    <div className={styles.headerInfo}>
                        {name && <h3 className={styles.itemName}>{name}</h3>}
                        <div className={styles.detailsContainer}>
                            {status && <OrderStatus status={status} />}
                            {rxContextualMessage && (
                                <div className={styles.instructions}>
                                    <p className={`${styles.instructionsText} ${status === 'Approved' ? styles.approved : status === 'Declined' ? styles.declined : ''}`}>{rxContextualMessage}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {mappedTags.length > 0 && (
                    <div className={styles.tagsContainer}>
                        <Tags tags={mappedTags} />
                    </div>
                )}
                {hasAdditionalMetaData && (
                    <div className={styles.additionalInfo}>
                        <AdditionalInfo additionalInfo={additionalMetaData} type="pharmacy" />
                    </div>
                )}
                {hasPrescriptionDetails && (
                    <div className={styles.prescriptionDetails}>
                        <div className={styles.prescriptionDetail}>
                            <span className={styles.prescriptionDetailLabel}>QTY remaining</span>
                            <span className={styles.prescriptionDetailValue}>{formatPrescriptionDetailValue(quantityRemaining)}</span>
                        </div>
                        <div className={styles.prescriptionDetail}>
                            <span className={styles.prescriptionDetailLabel}>{expirationLabel}</span>
                            <span className={styles.prescriptionDetailValue}>{formatExpirationDate(expirationTime)}</span>
                        </div>
                    </div>
                )}
                {hasActions && (
                    <div className={styles.actions}>
                        {actions.map((action, index) => (
                            <ActionButton
                                key={index}
                                widgetData={cardData}
                                action={action}
                            />
                        ))}
                    </div>
                )}
            </div>
        </WidgetContainer>
    );
}
